import { useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';

const DEFAULT_PAGE = 1;
const DEFAULT_LIMIT = 20;

export interface UsePaginationOptions {
  defaultPage?: number;
  defaultLimit?: number;
  pageParam?: string;
  limitParam?: string;
}

export interface UsePaginationReturn {
  page: number;
  limit: number;
  setPage: (page: number) => void;
  setLimit: (limit: number) => void;
  handleChange: (page: number, pageSize: number) => void;
}

export const usePagination = (options?: UsePaginationOptions): UsePaginationReturn => {
  const {
    defaultPage = DEFAULT_PAGE,
    defaultLimit = DEFAULT_LIMIT,
    pageParam = 'page',
    limitParam = 'limit',
  } = options ?? {};

  const [searchParams, setSearchParams] = useSearchParams();

  const page = Number(searchParams.get(pageParam)) || defaultPage;
  const limit = Number(searchParams.get(limitParam)) || defaultLimit;

  const updateParams = useCallback(
    (newPage: number, newLimit: number) => {
      const params = new URLSearchParams(searchParams);

      if (newPage === defaultPage) {
        params.delete(pageParam);
      } else {
        params.set(pageParam, String(newPage));
      }

      if (newLimit === defaultLimit) {
        params.delete(limitParam);
      } else {
        params.set(limitParam, String(newLimit));
      }

      setSearchParams(params);
    },
    [searchParams, setSearchParams, defaultPage, defaultLimit, pageParam, limitParam]
  );

  const setPage = useCallback(
    (newPage: number) => {
      updateParams(newPage, limit);
    },
    [updateParams, limit]
  );

  const setLimit = useCallback(
    (newLimit: number) => {
      updateParams(defaultPage, newLimit);
    },
    [updateParams, defaultPage]
  );

  const handleChange = useCallback(
    (newPage: number, newPageSize: number) => {
      if (newPageSize !== limit) {
        updateParams(defaultPage, newPageSize);
      } else {
        updateParams(newPage, newPageSize);
      }
    },
    [updateParams, limit, defaultPage]
  );

  return {
    page,
    limit,
    setPage,
    setLimit,
    handleChange,
  };
};
